import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  ROTULO_DA_OCORRENCIA,
  TIPOS_DE_OCORRENCIA,
  bN,
  diaCurto,
  lerOcorrencias,
  resolverOcorrencia,
} from '../../giro';
import type { FilaDeOcorrencias, Ocorrencia, TipoDeOcorrencia } from '../../giro';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * OCORRÊNCIAS DO CORREDOR (§37 do 1.0) — o que a equipe achou na prateleira e que não é contagem:
 * etiqueta ausente, preço divergente, avaria, validade… A fila é do administrador; fechar exige
 * dizer o que foi feito (fechar em branco apaga a razão de a ocorrência existir). Nada é apagado.
 */

type Situacao = 'abertas' | 'resolvidas';

const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function OcorrenciasDoGiro({ sessao }: { sessao: Sessao }) {
  const [situacao, setSituacao] = useState<Situacao>('abertas');
  const [d, setD] = useState<FilaDeOcorrencias | null>(null);
  const [erro, setErro] = useState(false);
  const [filtro, setFiltro] = useState<TipoDeOcorrencia | null>(null);
  const [abrindo, setAbrindo] = useState<string | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerOcorrencias(situacao).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, [situacao]);
  useEffect(carregar, [carregar]);

  const linhas = d?.ocorrencias.filter((o) => !filtro || o.tipo === filtro) ?? [];
  return (
    <section className="cv gr">
      <SubnavDoGiro
        atual="giro-ocorrencias"
        direita={d ? `${String(d.abertas)} em aberto` : undefined}
      />
      <div className="cv-corpo">
        <div className="cv-titulo cv-titulo-2l">
          <h1>Ocorrências do corredor</h1>
          <span>
            Registradas pela equipe durante a missão · não mudam a contagem · fechar pede o que foi
            feito
          </span>
        </div>
        {erro ? (
          <ErroDeArea oQue="as ocorrências" aoTentar={carregar} />
        ) : (
          <>
            <div className="cv-chips">
              {(['abertas', 'resolvidas'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`cv-chip ${situacao === s ? 'cv-chip-sel' : ''}`}
                  onClick={() => {
                    setSituacao(s);
                    setFiltro(null);
                  }}
                >
                  {s === 'abertas' ? 'Em aberto' : 'Resolvidas'}
                  {s === 'abertas' && d ? (
                    <>
                      {' '}
                      · <b>{d.abertas}</b>
                    </>
                  ) : null}
                </button>
              ))}
              {situacao === 'abertas' &&
                d &&
                TIPOS_DE_OCORRENCIA.filter((t) => d.porTipo[t.tipo]).map((t) => (
                  <button
                    key={t.tipo}
                    type="button"
                    className={`cv-chip ${filtro === t.tipo ? 'cv-chip-sel' : ''}`}
                    onClick={() => setFiltro(filtro === t.tipo ? null : t.tipo)}
                  >
                    {t.rotulo} <b>{d.porTipo[t.tipo]}</b>
                  </button>
                ))}
            </div>
            {!d ? (
              <div className="cv-carregando" aria-busy="true" />
            ) : (
              <div className="cv-cartao cv-sem-pad">
                {linhas.length === 0 ? (
                  <p className="cv-vazio">
                    {situacao === 'abertas'
                      ? 'Nenhuma ocorrência em aberto. O que a equipe registrar no corredor aparece aqui.'
                      : 'Nenhuma ocorrência resolvida ainda.'}
                  </p>
                ) : (
                  <div className="cv-rolagem">
                    <table className="cv-tabela">
                      <thead>
                        <tr>
                          <th>Ocorrência</th>
                          <th>Produto</th>
                          <th>Onde</th>
                          <th>Quem · quando</th>
                          <th>{situacao === 'abertas' ? '' : 'Resolução'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map((o) => (
                          <Linha
                            key={o.id}
                            o={o}
                            sessao={sessao}
                            aberta={abrindo === o.id}
                            aoAbrir={() => setAbrindo(abrindo === o.id ? null : o.id)}
                            aoResolver={() => {
                              setAbrindo(null);
                              carregar();
                            }}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Linha({
  o,
  sessao,
  aberta,
  aoAbrir,
  aoResolver,
}: {
  o: Ocorrencia;
  sessao: Sessao;
  aberta: boolean;
  aoAbrir: () => void;
  aoResolver: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const fechar = async () => {
    if (enviando) return;
    setEnviando(true);
    setAviso(null);
    const r = await resolverOcorrencia(sessao, o.id, texto.trim());
    setEnviando(false);
    if (r.tipo === 'confirmada') aoResolver();
    else
      setAviso(
        r.tipo === 'recusada'
          ? r.mensagem
          : 'Não deu para confirmar se fechou. Recarregue a lista e confira.',
      );
  };
  return (
    <>
      <tr>
        <td>
          <div className="cv-forte">{ROTULO_DA_OCORRENCIA[o.tipo]}</div>
          {(o.valor ?? o.detalhe) && (
            <div className="cv-cinza cv-peq">
              {[o.valor, o.detalhe].filter(Boolean).join(' · ')}
            </div>
          )}
        </td>
        <td>
          {o.produto ? (
            <>
              <div>{o.produto}</div>
              {o.codigo && <div className="cv-mono cv-cinza cv-peq">{o.codigo}</div>}
            </>
          ) : (
            <span className="cv-cinza">do bloco (sem produto)</span>
          )}
        </td>
        <td>
          {o.setor} · {bN(o.bloco)}
          <div className="cv-cinza cv-peq">operação de {diaCurto(o.dia)}</div>
        </td>
        <td>
          {o.quem ?? '—'}
          <div className="cv-mono cv-cinza cv-peq">{quando(o.em)}</div>
        </td>
        <td className="cv-dir">
          {o.resolvidoEm ? (
            <div style={{ textAlign: 'left' }}>
              <div>{o.resolucao}</div>
              <div className="cv-cinza cv-peq">
                {o.resolvidoPor ?? '—'} · {quando(o.resolvidoEm)}
              </div>
            </div>
          ) : (
            <button type="button" className="cv-botao" onClick={aoAbrir} aria-expanded={aberta}>
              <Icone nome="check" tamanho={13} traco={1.8} />
              Resolver
            </button>
          )}
        </td>
      </tr>
      {aberta && !o.resolvidoEm && (
        <tr>
          <td colSpan={5}>
            <div className="gc-campo" style={{ gap: 8 }}>
              <span>
                O que foi feito? <b>obrigatório</b>
              </span>
              <textarea
                rows={2}
                maxLength={500}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Ex.: etiqueta impressa e colocada na gôndola"
                autoFocus
              />
              <div className="gc-acoes">
                <button
                  type="button"
                  className="cv-botao cv-botao-primario"
                  disabled={texto.trim().length < 3 || enviando}
                  onClick={() => void fechar()}
                >
                  {enviando ? 'Fechando…' : 'Fechar ocorrência'}
                </button>
                <button type="button" className="cv-botao" onClick={aoAbrir}>
                  Cancelar
                </button>
              </div>
              {aviso && (
                <p className="gc-erro" role="alert">
                  {aviso}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
